import { z } from "zod";
import DOMPurify from "dompurify";

// Sanitization utility
export const sanitizeString = (input: string): string => {
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
};

// Common validation schemas
export const clienteSchema = z.object({
  nome_completo: z.string()
    .min(1, "Nome é obrigatório")
    .max(255, "Nome muito longo")
    .transform(sanitizeString),
  tipo_cliente: z.enum(["pessoa_fisica", "pessoa_juridica"], {
    message: "Tipo de cliente é obrigatório"
  }),
  cpf_cnpj: z.string()
    .min(11, "CPF/CNPJ inválido")
    .max(18, "CPF/CNPJ inválido")
    .transform(sanitizeString),
  email: z.string()
    .email("Email inválido")
    .max(255, "Email muito longo")
    .transform(sanitizeString),
  telefone: z.string()
    .min(10, "Telefone inválido")
    .max(15, "Telefone inválido")
    .transform(sanitizeString),
  endereco_logradouro: z.string()
    .min(1, "Logradouro é obrigatório")
    .max(255, "Logradouro muito longo")
    .transform(sanitizeString),
  endereco_numero: z.string()
    .min(1, "Número é obrigatório")
    .max(10, "Número muito longo")
    .transform(sanitizeString),
  endereco_bairro: z.string()
    .min(1, "Bairro é obrigatório")
    .max(100, "Bairro muito longo")
    .transform(sanitizeString),
  endereco_cidade: z.string()
    .min(1, "Cidade é obrigatória")
    .max(100, "Cidade muito longa")
    .transform(sanitizeString),
  endereco_uf: z.string()
    .length(2, "UF deve ter 2 caracteres")
    .transform(sanitizeString),
  endereco_cep: z.string()
    .length(9, "CEP inválido")
    .transform(sanitizeString),
  endereco_complemento: z.string()
    .max(255, "Complemento muito longo")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  observacoes: z.string()
    .max(1000, "Observações muito longas")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
});

export const financeiroSchema = z.object({
  nome: z.string()
    .min(1, "Nome é obrigatório")
    .max(255, "Nome muito longo")
    .transform(sanitizeString),
  tipo: z.enum(["receita", "despesa"], {
    message: "Tipo é obrigatório"
  }),
  categoria: z.string()
    .min(1, "Categoria é obrigatória")
    .max(100, "Categoria muito longa")
    .transform(sanitizeString),
  valor: z.number()
    .positive("Valor deve ser positivo")
    .max(999999999, "Valor muito alto"),
  data: z.date(),
  descricao: z.string()
    .max(1000, "Descrição muito longa")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  vencimento: z.date().optional(),
  recorrente: z.boolean().optional(),
  status: z.enum(["pendente", "pago", "cancelado"]).optional(),
});

export const projetoSchema = z.object({
  name: z.string()
    .min(1, "Nome é obrigatório")
    .max(255, "Nome muito longo")
    .transform(sanitizeString),
  client: z.string()
    .min(1, "Cliente é obrigatório")
    .max(255, "Nome do cliente muito longo")
    .transform(sanitizeString),
  description: z.string()
    .max(1000, "Descrição muito longa")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  location: z.string()
    .max(255, "Localização muito longa")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  value: z.number()
    .positive("Valor deve ser positivo")
    .max(999999999, "Valor muito alto")
    .optional(),
  start_date: z.date().optional(),
  end_date: z.date().optional(),
  status: z.enum(["planejamento", "em_andamento", "concluido", "cancelado"]).optional(),
});

// Schema para login - mais permissivo
export const loginSchema = z.object({
  email: z.string()
    .email("Email inválido")
    .max(255, "Email muito longo")
    .transform(sanitizeString),
  password: z.string()
    .min(1, "Senha é obrigatória")
    .max(128, "Senha muito longa"),
});

// Schema para cadastro - mais restritivo
export const registerSchema = z.object({
  email: z.string()
    .email("Email inválido")
    .max(255, "Email muito longo")
    .transform(sanitizeString),
  password: z.string()
    .min(8, "Senha deve ter pelo menos 8 caracteres")
    .max(128, "Senha muito longa")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Senha deve conter pelo menos: 1 letra minúscula, 1 maiúscula e 1 número"
    ),
});

// Schema genérico para autenticação (mantido para compatibilidade)
export const authSchema = loginSchema;

// File upload validation
export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  return allowedTypes.some(type => file.type.startsWith(type));
};

export const validateFileSize = (file: File, maxSizeMB: number): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ALLOWED_DOCUMENT_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
export const MAX_FILE_SIZE_MB = 10;

// Schema para perfil empresarial (Configurações)
export const empresaPerfilSchema = z.object({
  nomeEmpresa: z.string()
    .min(3, "Nome da empresa deve ter pelo menos 3 caracteres")
    .max(255, "Nome muito longo")
    .transform(sanitizeString),
  cnpj: z.string()
    .regex(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/, "CNPJ inválido (formato: 00.000.000/0000-00)")
    .optional()
    .or(z.literal("")),
  email: z.string()
    .email("Email inválido")
    .max(255, "Email muito longo")
    .transform(sanitizeString),
  telefone: z.string()
    .regex(/^\(\d{2}\) \d{5}-\d{4}$/, "Telefone inválido (formato: (00) 00000-0000)")
    .optional()
    .or(z.literal("")),
  cep: z.string()
    .regex(/^\d{5}-\d{3}$/, "CEP inválido (formato: 00000-000)")
    .optional()
    .or(z.literal("")),
  logradouro: z.string()
    .max(255, "Logradouro muito longo")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  numero: z.string()
    .max(10, "Número muito longo")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  complemento: z.string()
    .max(255, "Complemento muito longo")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  bairro: z.string()
    .max(100, "Bairro muito longo")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  cidade: z.string()
    .max(100, "Cidade muito longa")
    .optional()
    .transform((val) => val ? sanitizeString(val) : val),
  uf: z.string()
    .length(2, "UF deve ter 2 caracteres")
    .optional()
    .or(z.literal("")),
});

export type ClienteFormData = z.infer<typeof clienteSchema>;
export type FinanceiroFormData = z.infer<typeof financeiroSchema>;
export type ProjetoFormData = z.infer<typeof projetoSchema>;
export type AuthFormData = z.infer<typeof authSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type EmpresaPerfilFormData = z.infer<typeof empresaPerfilSchema>;